import type { TestableComponent } from '@mweb/types';
import { Drawer } from '@mweb/zappos-ui/Drawer';
import { useEffect, useState } from 'react';

import { track } from 'apis/amethyst';
import { TabsContainer } from 'components/productdetail/ReviewSummarization/TabsContainer/TabsContainer';
import { PRODUCT_PAGE, REVIEW_SUMMARY_MODAL } from 'constants/amethystPageTypes';
import { evReviewSummaryAttributeClick } from 'events/productReview';
import { useFetchReviewSummarization } from 'hooks/useFetchReviewSummarization';
import useMartyContext from 'hooks/useMartyContext';
import useWindowSize from 'hooks/useWindowSize';
import { isMobileViewport } from 'utils/viewportUtil';
import { AI_GENERATED, CUSTOMERS_SAY, LEARN_MORE } from 'constants/reviews';

export interface ReviewSummarizationProps extends React.ComponentPropsWithoutRef<'div'>, TestableComponent {
  productId: string;
  /** Callback to let a parent page know this component will render or not */
  setIsRendered: (isRendered: boolean) => void;
}

export const ReviewSummarization = (props: ReviewSummarizationProps) => {
  const { testId, productId, setIsRendered } = props;
  const { testId: getTestId } = useMartyContext();
  const { width: windowWidth = 0 } = useWindowSize(200);
  const isMobile = isMobileViewport(windowWidth);
  const [selectedTab, setSelectedTab] = useState('');

  const { reviewSummarization, isFetched } = useFetchReviewSummarization({ productId: productId }) ?? {};
  const { summary, aspects } = reviewSummarization ?? {};

  useEffect(() => {
    // If the call is finished and there is no summary we know this component won't render
    if (!summary && isFetched) {
      setIsRendered(false);
    }
  }, [summary, isFetched]);

  const trackModalClick = (tab: string) => {
    track(() => [evReviewSummaryAttributeClick, { productId, attribute: tab, sourcePage: REVIEW_SUMMARY_MODAL }]);
  };

  const onTabOpen = (tab: string) => {
    setSelectedTab(tab);
    // if tab is being closed, no event to fire.
    if (tab) {
      track(() => [evReviewSummaryAttributeClick, { productId, attribute: tab, sourcePage: PRODUCT_PAGE }]);
    }
  };

  const closeDrawer = () => {
    setSelectedTab('');
  };

  return (
    <div className="flex max-w-4xl flex-col gap-y-4" data-test-id={getTestId(testId)}>
      <h3 className="text-2xl font-medium">{CUSTOMERS_SAY}</h3>
      <p className="text-base">{summary}</p>
      {aspects?.length && (
        <div className="flex flex-col gap-6">
          <p className="text-base text-tertiary">{AI_GENERATED}</p>
          <div className="flex flex-col gap-y-2">
            <p className="text-base font-medium">{LEARN_MORE}</p>
            <TabsContainer
              testId={`${getTestId(testId)}-container`}
              aspects={aspects}
              onTabOpen={onTabOpen}
              selectedTab={selectedTab}
              isSuppressContent={isMobile}
            />
          </div>
        </div>
      )}
      {aspects?.length && isMobile && selectedTab && (
        <Drawer open={selectedTab ? true : false} onOpenChange={closeDrawer} position="bottom" title="">
          <TabsContainer
            id="mobileTabContainer"
            isWrapHeaderEnabled={false}
            testId={`${getTestId(testId)}-container-drawer`}
            aspects={aspects}
            selectedTab={selectedTab}
            onTabOpen={trackModalClick}
          />
        </Drawer>
      )}
    </div>
  );
};
